import { AbstractControl } from '@angular/forms';
import { ValidationErrors as NgValidationErrors } from '@angular/forms';

import { camelCase, get } from 'lodash-es';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ApiError, Error } from '../models';
import { isNullish } from './types';

export type ValidationError = Error;
export type ValidationErrors = ValidationError[];
export type AsyncValidationErrors = Observable<ValidationErrors>;
export interface ValidatorFn {
	(value: any): ValidationErrors;
}
export interface AsyncValidatorFn {
	(value: any): Promise<ValidationErrors> | Observable<ValidationErrors>;
}

export const REGEXP_EMAIL =
	/^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export const REGEXP_BANK_ACCOUNT_NUMBER = /^\d{8}[- ]?\d{8}(?:[- ]?\d{8})?$/;

export const REGEXP_ZIPCODE = /^\d{4}$/;

export const REGEXP_HOUSE_NUMBER = /^[1-9]\d*\.?$/;

export const REGEXP_POBOX_NUMBER = /^[1-9]\d*$/;

export const REGEXP_TAX_NUMBER = /^\d{8}-\d-\d{2}$/;

export const REGEXP_COMPANY_NAME = /\s+((ny|z)?rt|kft|bt|e[\s\.]*v|v[áa]llalkoz[óo])\.?\s*$/i;

const generatePhoneRegexp = (countryCode: string, areaCodes: string[], minLength: number, maxLength: number) => {
	return {
		full: new RegExp(`^(?:00|\\+)${countryCode}(?:${areaCodes.join('|')})\\d{${minLength},${maxLength}}$`),
		national: new RegExp(`^(?:${areaCodes.join('|')})\\d{${minLength},${maxLength}}$`),
	};
};

export const REGEXP_MYGLS_PHONE = {
	any: {
		regexp: { full: /^(?:00|\+)\d{7,}$/, national: /^\d{7,}$/ },
		example: '+36201234567',
	},
	hu: {
		regexp: generatePhoneRegexp('36', ['20', '30', '31', '70', '50'], 7, 7),
		example: '+36201234567',
	},
	sk: {
		regexp: generatePhoneRegexp(
			'421',
			[
				'901',
				'902',
				'903',
				'904',
				'905',
				'906',
				'907',
				'908',
				'909',
				'910',
				'911',
				'912',
				'913',
				'914',
				'915',
				'916',
				'917',
				'918',
				'919',
				'930',
				'931',
				'932',
				'933',
				'934',
				'935',
				'936',
				'937',
				'938',
				'939',
				'940',
				'941',
				'942',
				'943',
				'944',
				'945',
				'946',
				'947',
				'948',
				'949',
				'950',
				'951',
				'952',
				'953',
				'954',
				'955',
				'956',
				'957',
				'958',
			],
			6,
			6
		),
		example: '+421901123456',
	},
	cz: {
		regexp: generatePhoneRegexp(
			'420',
			[
				'600',
				'601',
				'602',
				'603',
				'604',
				'605',
				'606',
				'607',
				'608',
				'702',
				'703',
				'704',
				'705',
				'720',
				'721',
				'722',
				'723',
				'724',
				'725',
				'726',
				'727',
				'728',
				'729',
				'730',
				'731',
				'732',
				'733',
				'734',
				'735',
				'736',
				'737',
				'738',
				'739',
				'770',
				'771',
				'772',
				'773',
				'774',
				'775',
				'776',
				'777',
				'778',
				'779',
				'790',
				'791',
				'792',
				'793',
				'794',
				'795',
				'796',
				'797',
				'798',
				'799',
			],
			6,
			6
		),
		example: '+420600123456',
	},
	ro: {
		regexp: generatePhoneRegexp(
			'40',
			[
				'700',
				'701',
				'702',
				'703',
				'704',
				'705',
				'706',
				'707',
				'708',
				'709',
				'710',
				'711',
				'712',
				'713',
				'714',
				'715',
				'716',
				'717',
				'718',
				'719',
				'720',
				'721',
				'722',
				'723',
				'724',
				'725',
				'726',
				'727',
				'728',
				'729',
				'730',
				'731',
				'732',
				'733',
				'734',
				'735',
				'736',
				'737',
				'738',
				'739',
				'740',
				'741',
				'742',
				'743',
				'744',
				'745',
				'746',
				'747',
				'748',
				'749',
				'750',
				'751',
				'752',
				'753',
				'754',
				'755',
				'756',
				'757',
				'758',
				'759',
				'760',
				'761',
				'762',
				'763',
				'764',
				'765',
				'766',
				'767',
				'768',
				'769',
				'770',
				'771',
				'772',
				'773',
				'774',
				'775',
				'776',
				'777',
				'778',
				'779',
				'780',
				'781',
				'782',
				'783',
				'784',
				'785',
				'786',
				'787',
				'788',
				'789',
				'790',
				'791',
				'792',
				'793',
				'794',
				'795',
				'796',
				'797',
				'798',
				'799',
			],
			6,
			6
		),
		example: '+40700123456',
	},
	si: {
		regexp: generatePhoneRegexp('386', ['30', '31', '40', '41', '51', '64', '68', '69', '70', '71'], 6, 6),
		example: '+38630123456',
	},
	hr: {
		regexp: generatePhoneRegexp('385', ['91', '92', '95', '97', '98', '99'], 6, 7),
		example: '+385911234567',
	},
};

function isEmptyValue(value: any): boolean {
	return isNullish(value) || value.length === 0;
}

function hasValidLength(value: any): boolean {
	return !isNullish(value) && typeof value.length === 'number';
}

export class Validators {
	static getMyglsPhoneNumberIsoCodes = () => Object.keys(REGEXP_MYGLS_PHONE) as (keyof typeof REGEXP_MYGLS_PHONE)[];
	static getMyglsPhoneNumberExample = (isoCode: keyof typeof REGEXP_MYGLS_PHONE) => REGEXP_MYGLS_PHONE[isoCode].example;

	static _required =
		({
			code = 'required',
		}: {
			code?: string;
		} = {}) =>
		(value: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			if (isEmptyValue(value)) {
				errors.push({
					code,
				});
			}

			return errors;
		};
	static required = Validators._required();

	static _requiredTrue =
		({
			code = 'required',
		}: {
			code?: string;
		} = {}) =>
		(value: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			if (value !== true) {
				errors.push({
					code,
				});
			}

			return errors;
		};
	static requiredTrue = Validators._requiredTrue();

	static _min =
		(
			min: number,
			{
				code = 'min',
			}: {
				code?: string;
			} = {}
		) =>
		(value: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			// don't validate empty values to allow optional controls
			if (isEmptyValue(value) || isEmptyValue(min)) {
				return errors;
			}
			const actual = parseFloat(value);

			// Controls with NaN values after parsing should be treated as not having a
			// minimum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
			if (!isNaN(actual) && actual < min) {
				errors.push({
					code,
					data: { min, actual },
				});
			}

			return errors;
		};
	static min = (min: number) => Validators._min(min);

	static _max =
		(
			max: number,
			{
				code = 'max',
			}: {
				code?: string;
			} = {}
		) =>
		(value: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			// don't validate empty values to allow optional controls
			if (isEmptyValue(value) || isEmptyValue(max)) {
				return errors;
			}
			const actual = parseFloat(value);

			// Controls with NaN values after parsing should be treated as not having a
			// maximum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-max
			if (!isNaN(actual) && actual > max) {
				errors.push({
					code,
					data: { max, actual },
				});
			}

			return errors;
		};
	static max = (max: number) => Validators._max(max);

	static _minLength =
		(
			minLength: number,
			{
				code = 'minLength',
			}: {
				code?: string;
			} = {}
		) =>
		(value: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			if (isEmptyValue(value) || !hasValidLength(value)) {
				return errors;
			}

			if (value.length < minLength) {
				errors.push({
					code,
					data: { minLength, actualLength: value.length },
				});
			}

			return errors;
		};
	static minLength = (minLength: number) => Validators._minLength(minLength);

	static _maxLength =
		(
			maxLength: number,
			{
				code = 'maxLength',
			}: {
				code?: string;
			} = {}
		) =>
		(value: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			if (hasValidLength(value) && value.length > maxLength) {
				errors.push({
					code,
					data: { maxLength, actualLength: value.length },
				});
			}

			return errors;
		};
	static maxLength = (maxLength: number) => Validators._maxLength(maxLength);

	static _number =
		({
			code = 'number',
		}: {
			code?: string;
		} = {}) =>
		(value: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			if (typeof value !== 'number') {
				errors.push({
					code,
				});
			}

			return errors;
		};
	static number = Validators._number();

	static _integer =
		({
			code = 'integer',
		}: {
			code?: string;
		} = {}) =>
		(value: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			if (!Number.isInteger(value)) {
				errors.push({
					code,
				});
			}

			return errors;
		};
	static integer = Validators._integer();

	static _pattern = (
		pattern: string | RegExp,
		{
			code = 'pattern',
			negativeMatch = false,
		}: {
			code?: string;
			negativeMatch?: boolean;
		} = {}
	) => {
		let regex: RegExp;
		if (typeof pattern === 'string') {
			let regexStr: string = '';
			if (pattern.charAt(0) !== '^') regexStr += '^';
			regexStr += pattern;
			if (pattern.charAt(pattern.length - 1) !== '$') regexStr += '$';
			regex = new RegExp(regexStr);
		} else {
			regex = pattern;
		}

		return (value: string): ValidationErrors => {
			const errors: ValidationErrors = [];

			if (isEmptyValue(value)) {
				return errors;
			}

			if (negativeMatch) {
				if (regex.test(value)) {
					errors.push({
						code,
					});
				}
			} else {
				if (!regex.test(value)) {
					errors.push({
						code,
					});
				}
			}

			return errors;
		};
	};
	static pattern = (pattern: string | RegExp) => Validators._pattern(pattern);

	static email = Validators._pattern(REGEXP_EMAIL, { code: 'email' });

	static phoneNumber = (type: 'full' | 'national' = 'full') => Validators._pattern(REGEXP_MYGLS_PHONE['hu'].regexp[type], { code: 'phoneNumber' });
	static myglsPhoneNumber =
		(country?: keyof typeof REGEXP_MYGLS_PHONE, type: 'full' | 'national' = 'full') =>
		(value: any): ValidationErrors => {
			if (country) {
				return Validators._pattern(REGEXP_MYGLS_PHONE[country].regexp[type], {
					code: `myglsPhoneNumber.${country}`,
				})(value);
			} else {
				const errors: ValidationErrors = [];

				if (isEmptyValue(value)) {
					return errors;
				}

				const hasMatch = Object.values(REGEXP_MYGLS_PHONE).some(regexp => regexp.regexp[type].test(value));

				if (!hasMatch) {
					errors.push({
						code: 'myglsPhoneNumber',
					});
				}

				return errors;
			}
		};

	static bankAccountNumber = Validators._pattern(REGEXP_BANK_ACCOUNT_NUMBER, { code: 'bankAccountNumber' });
	static taxNumber = Validators._pattern(REGEXP_TAX_NUMBER, { code: 'taxNumber' });
	static zipCode = Validators._pattern(REGEXP_ZIPCODE, { code: 'zipCode' });
	static houseNumber = Validators._pattern(REGEXP_HOUSE_NUMBER, { code: 'houseNumber' });
	static poBoxNumber = Validators._pattern(REGEXP_POBOX_NUMBER, { code: 'poBoxNumber' });
	static notCompanyName = Validators._pattern(REGEXP_COMPANY_NAME, { code: 'notCompanyName', negativeMatch: true });

	static passwordStrength = (password: string): ValidationErrors => {
		const errors: ValidationErrors = [];

		if (isEmptyValue(password)) {
			return errors;
		}

		const constraints = {
			length: password.length >= 8,
			number: /\d/.test(password),
			lowercase: /[a-z]/.test(password),
			uppercase: /[A-Z]/.test(password),
			symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
		};
		Object.entries(constraints).forEach(([constraint, isValid]) => {
			if (!isValid) {
				errors.push({
					code: `weakPassword:${constraint}`,
				});
			}
		});

		return errors;
	};

	static matchingPasswords =
		(passwordPath: string = 'password', passwordConfirmationPath: string = 'passwordConfirmation') =>
		(parentObj: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			const password = get(parentObj, passwordPath);
			const passwordConfirmation = get(parentObj, passwordConfirmationPath);

			if (isEmptyValue(password)) {
				return errors;
			}

			if (password !== passwordConfirmation) {
				errors.push({
					code: 'passwordMismatch',
				});
			}

			return errors;
		};

	static compose =
		(validators: ValidatorFn[]) =>
		(value: any): ValidationErrors => {
			return validators.reduce((errors, validator) => [...errors, ...validator(value)], [] as ValidationErrors);
		};

	static execute =
		(validators: ValidatorFn[]) =>
		(value: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			for (let validator of validators) {
				errors.push(...validator(value));
				if (errors.length > 0) break;
			}

			return errors;
		};

	static composeAsync =
		(validators: AsyncValidatorFn[]) =>
		(value: any): Observable<Error[]> => {
			return combineLatest([...validators.map(validator => validator(value))]).pipe(
				map((errors: ValidationErrors[]) => errors.reduce((acc, errors) => [...acc, ...errors], [] as ValidationErrors))
			);
		};

	static conditionalValidator =
		(predicateInput: any, predicate: (predicateInput: any) => boolean, validators: ValidatorFn[]) =>
		(value: any): ValidationErrors => {
			let condition: boolean;
			try {
				condition = predicate(predicateInput);
			} catch (e) {
				console.warn(e);
				return [
					{
						code: 'conditionalValidator.predicateException',
						data: { predicateInput, predicate, validators },
					},
				];
			}
			return condition ? Validators.execute(validators)(value) : [];
		};
}

export interface ObjectValidatorOptions {
	condition?: {
		path: string;
		predicate: (predicateInput: any) => boolean;
	};
}
export interface ObjectValidatorConfig {
	path: string;
	validators: ValidatorFn[];
	// asyncValidators?: AsyncValidatorFn[],
	opts?: ObjectValidatorOptions;
}

export class ObjectValidator {
	// private readonly validators: Record<string, ValidatorFn[]> = {};
	private readonly validators: Record<
		string,
		{
			validators: ValidatorFn[];
			opts: ObjectValidatorOptions;
		}[]
	> = {};

	// private readonly asyncValidators: Record<string,
	// 	{
	// 		asyncValidators: AsyncValidatorFn[],
	// 		opts: ObjectValidatorOptions,
	// 	}[]
	// > = {};

	constructor(validators: ObjectValidatorConfig[] = []) {
		this.add(validators);
	}

	public add(validators: ObjectValidatorConfig[]) {
		validators.forEach(validator => {
			const {
				path,
				validators = [],
				// asyncValidators = [],
				opts = {},
			} = validator;

			if (!this.validators[path]) this.validators[path] = [];
			this.validators[path].push({ validators, opts });

			// if (!this.asyncValidators[path]) this.asyncValidators[path] = [];
			// this.asyncValidators[path].push({ asyncValidators, opts });
		});
	}

	public validate(obj: Record<string | number, any>) {
		const results: Record<string, ValidationErrors> = {};

		Object.keys(this.validators).forEach(path => {
			if (!results[path]) results[path] = [];

			this.validators[path].forEach(validatorsGroup => {
				const { validators, opts } = validatorsGroup;

				let shouldValidate: boolean = true;

				if (opts.condition) {
					const condition = opts.condition;
					shouldValidate = false;

					// console.log('conditionalValidator', condition, validators);

					try {
						const predicateObj = !isEmptyValue(condition.path) ? get(obj, condition.path) : obj;

						// console.log('conditionalValidator predicateObj', predicateObj);

						shouldValidate = condition.predicate(predicateObj);
					} catch (e) {
						console.warn(e);
						results[path].push({
							code: 'conditionalValidator.predicateException',
							data: { condition, validators },
						});
					}

					// console.log('conditionalValidator predicateResult', shouldValidate);
				}

				if (shouldValidate) {
					const value = !isEmptyValue(path) ? get(obj, path) : obj;
					// console.log('validator executing', value, validators);
					results[path].push(...Validators.execute(validators)(value));
				}
			});
		});

		return results;
	}

	public getErrors(obj: Record<string | number, any>) {
		return this.validate(obj);
	}

	public isValid(obj: Record<string | number, any>) {
		const result = this.validate(obj);
		const valid = Object.values(result).every(errors => errors.length === 0);
		return valid;
	}
}

export class AsyncObjectValidator extends ObjectValidator {
	constructor(private obj$: Observable<Record<string | number, any>>, validators: ObjectValidatorConfig[] = []) {
		super(validators);
	}

	public readonly errors$: Observable<Record<string, ValidationErrors>> = this.obj$.pipe(
		map(obj => this.validate(obj)),
		shareReplay(1)
	);

	public readonly flattenedErrors$ = this.errors$.pipe(
		map(errors => {
			const flattenedErrors: ValidationErrors = [];
			Object.entries(errors).map(([path, errors]) => {
				flattenedErrors.push(
					...errors.map(error => ({
						...error,
						source: { parameters: [path] },
					}))
				);
			});
			return flattenedErrors;
		}),
		shareReplay(1)
	);

	public readonly isValid$: Observable<boolean> = this.errors$.pipe(
		map(errors => Object.values(errors).every(errors => errors.length === 0)),
		shareReplay(1)
	);
}

const mapErrorToNgValidationError = (error: Error): NgValidationErrors => ({ [error.code]: error.data ?? true });

export const mapApiErrorToObjectErrors = (apiErrors: ApiError[]): Record<string, ValidationErrors> => {
	const errors: Record<string, ValidationErrors> = {};
	apiErrors.forEach(apiError => {
		const source = apiError.source?.parameters?.[0];
		if (source) {
			const path = source.split('.').map(camelCase).join('.');
			if (!errors[path]) errors[path] = [];
			const code = apiError.code.split(':').map(camelCase).join(':');

			errors[path].push({
				code,
				data: apiError,
			});
		}
	});
	return errors;
};

export const remapObjectErrorPaths = (errors: Record<string, ValidationErrors>, map: Record<string, string>): Record<string, ValidationErrors> => {
	const remappedErrors: Record<string, ValidationErrors> = {};
	Object.keys(errors).forEach(oldPath => {
		let newPath = oldPath;

		const remap = Object.entries(map).find(([search, replace]) => oldPath.indexOf(search) === 0);
		if (remap) {
			newPath = oldPath.replace(remap[0], remap[1]);
		}

		remappedErrors[newPath] = errors[oldPath];
	});
	return remappedErrors;
};

export interface UpdateErrorsOnControlOptions {
	mode?: 'reset' | 'append';
	markAsTouched?: boolean;
	scrollToFirst?: boolean;
}

export const updateErrorsOnControl = (rootControl: AbstractControl, errors: Record<string, ValidationErrors>, opts: UpdateErrorsOnControlOptions = {}) => {
	opts = {
		mode: 'reset',
		markAsTouched: false,
		scrollToFirst: false,
		...opts,
	};
	let scrollToFirstMatched = false;

	Object.entries(errors).forEach(([path, errors]) => {
		const control = path.length > 0 ? rootControl.get(path) : rootControl;

		if (control) {
			if (errors.length > 0) {
				const ngErrors = errors.reduce((acc, error) => {
					acc = {
						...acc,
						...mapErrorToNgValidationError(error),
					};
					return acc;
				}, {} as NgValidationErrors);

				// console.log('updateErrorsOnControl', path, ngErrors, opts, control)

				if (opts.mode === 'append') {
					control.setErrors({
						...control.errors,
						...ngErrors,
					});
				} else {
					control.setErrors(ngErrors);
				}

				if (opts.scrollToFirst && !scrollToFirstMatched) {
					console.log('nativeElement', control, (control as any)?.nativeElement);
					((control as any)?.nativeElement as HTMLElement).scrollIntoView();
					scrollToFirstMatched = true;
				}
			} else {
				control.setErrors(null);
			}

			if (opts.markAsTouched) control.markAsTouched();
		}
	});
};
